import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { joinPath, type AccessAction, type AccessPerson } from '@bbbk/schema';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../common';
import { AccessColumn } from './AccessColumn';
import { AccessColumnSkeleton } from './AccessColumnSkeleton';

// The App access column, wired: one read on open, three writes behind it.
//
// The column itself is presentational and fully controlled (AccessColumn); this
// is where the household's people come from and where an action becomes a
// request. Split that way because the six states have to be renderable from
// props alone for the visual contract to be worth anything.

/**
 * Where a registration link points — this app's own origin.
 *
 * Guarded because the unit harness renders through `renderToStaticMarkup`,
 * where there is no `window` at all: reading it directly took the whole
 * household modal's test down with a ReferenceError. An empty origin yields a
 * relative `/join/<token>`, which is still a working link from the same site
 * and is the honest fallback rather than a guess at a hostname.
 */
function appOrigin(): string {
  return typeof window === 'undefined' ? '' : window.location.origin;
}

const ACTION_PATH: Record<AccessAction, string> = {
  new: 'links',
  reset: 'reset',
  revoke: 'revoke',
};

const DONE: Record<AccessAction, string> = {
  new: 'New link ready to copy',
  reset: 'Registration reset — a new link is ready',
  revoke: 'Access revoked',
};

/**
 * The column when there is no household on the modal yet.
 *
 * A household being created, or a viewing that has not become one. Freddi,
 * 2026-09-12, and the prototype's flow 1.1 agrees: it shows the section with its
 * own empty line — *Save the household and its people appear here* — rather than
 * a gap where the column will be. There is nothing to fetch and nobody to issue
 * a link to, so it is the presentational column with an empty list, not a panel
 * with a request in flight.
 */
export function AccessColumnForNewHousehold() {
  return (
    <AccessColumn
      people={[]}
      openId={null}
      manageId={null}
      confirm={null}
      pending={null}
      busy={null}
      origin={appOrigin()}
      onToggle={() => {}}
      onManage={() => {}}
      onConfirm={() => {}}
      onAction={() => {}}
    />
  );
}

/**
 * What the column lends the cards beside it (messages-one-shape): the
 * household's people as App access reads them, and the one way to make a
 * person's sign-up link — the column's own New link, so the link, its expiry,
 * its audit row and the row's state are exactly what a press of New link gives.
 */
export interface AccessLend {
  people: AccessPerson[] | null;
  issueLink: (contactId: number) => Promise<string | null>;
}

export function AccessPanel({ ownerId, lead, leadHeads, frame, draft }: { ownerId: string; lead?: ReactNode; leadHeads?: boolean; frame?: (rows: ReactNode, access: AccessLend) => ReactNode; draft?: (person: AccessPerson, url: string, registered: boolean) => string }) {
  const [people, setPeople] = useState<AccessPerson[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [manageId, setManageId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ contactId: number; action: AccessAction } | null>(null);
  // WHAT is in flight, not merely that something is: the column draws the draft
  // it is about to fill for an issue, and nothing for a revoke.
  const [pending, setPending] = useState<{ contactId: number; action: AccessAction } | null>(null);
  const toast = useToast();

  // `cache: 'reload'` on every read of this column, always. The body carries a
  // live registration token and the states move underneath it — a link expires
  // on a clock nobody presses, and a customer may finish registering while the
  // modal is open. A cached column would show Patricia a link that has died.
  // THE HOUSEHOLD ON SCREEN NOW (PR #267 review): a re-read for a household Patricia has since left
  // must not paint its people onto the next one's cards.
  const current = useRef(ownerId);
  current.current = ownerId;
  const load = useCallback(
    async (signal?: { cancelled: boolean }) => {
      try {
        const res = await api<AccessPerson[]>(`/owners/${ownerId}/access`, { cache: 'reload' });
        if (signal?.cancelled || current.current !== ownerId) return null;
        setPeople(res.data ?? []);
        return res.data ?? [];
      } catch (e) {
        if (signal?.cancelled || current.current !== ownerId) return null;
        // A 401 has already redirected inside the client. Anything else leaves
        // the column empty rather than taking the household's modal down with
        // it: the rest of the pane is still perfectly usable.
        if (!(e instanceof ApiError && e.status === 401)) setPeople([]);
        return null;
      }
    },
    [ownerId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    setPeople(null);
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  // ONE LINK PER PERSON PER OPEN (AC2a): two taps, or two cards, share the one request.
  const issuing = useRef(new Map<number, Promise<string | null>>());
  useEffect(() => { issuing.current = new Map(); }, [ownerId]);
  const issueLink = (contactId: number): Promise<string | null> => {
    const running = issuing.current.get(contactId);
    if (running) return running;
    const made = (async () => {
      try {
        await api(`/owners/${ownerId}/links`, { method: 'POST', body: { contactId } });
      } catch (e) {
        // Bound in the meantime (409): the re-read below says where they stand now.
        if (!(e instanceof ApiError && e.status === 409)) { issuing.current.delete(contactId); return null; }
      }
      const now = await load();
      const token = now?.find((p) => p.contactId === contactId)?.token;
      return token ? `${appOrigin()}${joinPath(token)}` : null;
    })().then((url) => {
      // A link that did not come back is not remembered (PR #267 review): the next tap tries again.
      if (!url) issuing.current.delete(contactId);
      return url;
    });
    issuing.current.set(contactId, made);
    return made;
  };

  const act = async (person: AccessPerson, action: AccessAction) => {
    setPending({ contactId: person.contactId, action });
    // The row the answer belongs to opens BEFORE the request goes out, so the
    // draft's shape is already on screen when the values arrive.
    if (action !== 'revoke') setOpenId(person.contactId);
    try {
      // NEW LINK SHARES THE TAP'S ONE REQUEST (PR #267 review): pressed beside a WhatsApp tap for the
      // same person, the two make one link, never two live ones.
      if (action === 'new') {
        if (!(await issueLink(person.contactId))) throw new Error('Could not make the link.');
      } else {
        await api(`/owners/${ownerId}/${ACTION_PATH[action]}`, {
          method: 'POST',
          body: { contactId: person.contactId },
        });
      }
      await load();
      // The row a link was just made for OPENS ITSELF. The draft is the entire
      // reason she pressed the button, and leaving it collapsed would make
      // "New link" a control whose result is hidden one tap away.
      setManageId(null);
      toast.success(DONE[action]);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return;
      // The server's own words where it has them — "There is no access to take
      // away for this person" is more use than a generic failure, and a 409
      // here means the column is showing something stale.
      toast.error(e instanceof ApiError && e.message ? e.message : 'Could not change app access.');
      await load();
    } finally {
      setPending(null);
    }
  };

  // The decision never waits on this read (PR #251 review): inside a frame,
  // the card draws at once and only the fold is a skeleton.
  // Inside its column from the first frame (2026-10-11): the cards never paint without the aside that holds them.
  // In a frame the column itself carries the loading (one tree, so the card
  // around the fold keeps its state when the people land); alone, the column's skeleton.
  if (people === null && !frame) return <AccessColumnSkeleton />;
  // The lead card (the request card on a booking) rides inside the column.

  return (
    <AccessColumn
      people={people}
      lead={lead}
      leadHeads={leadHeads}
      frame={frame ? (rows) => frame(rows, { people, issueLink }) : undefined}
      draft={draft}
      openId={openId}
      manageId={manageId}
      confirm={confirm}
      pending={pending}
      busy={pending?.contactId ?? null}
      origin={appOrigin()}
      onToggle={(id) => {
        setOpenId((cur) => (cur === id ? null : id));
        setManageId(null);
        setConfirm(null);
      }}
      onManage={(id) => setManageId((cur) => (cur === id ? null : id))}
      onConfirm={setConfirm}
      onAction={(person, action) => void act(person, action)}
    />
  );
}
